/**
 * Canvas 去背 Hook
 * 将品红色背景转为透明
 */
import { useState, useEffect, useRef } from 'react'
import { DEFAULT_SPRITE_CONFIG } from './types'

const SHEET_WIDTH = DEFAULT_SPRITE_CONFIG.cols * DEFAULT_SPRITE_CONFIG.frameWidth   // 1024
const SHEET_HEIGHT = DEFAULT_SPRITE_CONFIG.rows * DEFAULT_SPRITE_CONFIG.frameHeight // 896
const BATCH_SIZE = 50000 // 每批处理的像素数

// 品红色检测：R>150 且 G<100 且 B>150
function isMagenta(r: number, g: number, b: number): boolean {
  return r > 150 && g < 100 && b > 150
}

// 计算品红色相似度（用于边缘抗锯齿）
function magentaSimilarity(r: number, g: number, b: number): number {
  const rScore = Math.max(0, (r - 100) / 155)
  const gScore = Math.max(0, (100 - g) / 100)
  const bScore = Math.max(0, (b - 100) / 155)
  return rScore * gScore * bScore
}

export function useSpriteBackground(spriteSrc: string) {
  const [processedSrc, setProcessedSrc] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const processingRef = useRef(false)

  useEffect(() => {
    // 防止重复处理
    if (processingRef.current) return

    setIsProcessing(true)
    setProgress(0)
    setError(null)
    setProcessedSrc(null)
    processingRef.current = true

    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = SHEET_WIDTH
      canvas.height = SHEET_HEIGHT
      const ctx = canvas.getContext('2d')!

      ctx.drawImage(img, 0, 0, SHEET_WIDTH, SHEET_HEIGHT)
      const imageData = ctx.getImageData(0, 0, SHEET_WIDTH, SHEET_HEIGHT)
      const data = imageData.data
      const totalPixels = SHEET_WIDTH * SHEET_HEIGHT

      let processedPixels = 0

      // 使用 requestIdleCallback 分批处理
      function processBatch(deadline?: IdleDeadline) {
        const end = Math.min(processedPixels + BATCH_SIZE, totalPixels)

        while (processedPixels < end) {
          const i = processedPixels * 4
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]

          if (isMagenta(r, g, b)) {
            data[i + 3] = 0 // 完全透明
          } else {
            // 边缘抗锯齿：渐变过渡
            const similarity = magentaSimilarity(r, g, b)
            if (similarity > 0.3) {
              data[i + 3] = Math.round(255 * (1 - similarity))
            }
          }

          processedPixels++

          // 如果有 deadline 且时间不够了，让出线程
          if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 1) {
            break
          }
        }

        setProgress(Math.round((processedPixels / totalPixels) * 100))

        if (processedPixels < totalPixels) {
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(processBatch)
          } else {
            setTimeout(() => processBatch(), 0)
          }
        } else {
          // 处理完成
          ctx.putImageData(imageData, 0, 0)
          const resultUrl = canvas.toDataURL('image/png')
          setProcessedSrc(resultUrl)
          setIsProcessing(false)
          processingRef.current = false
        }
      }

      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(processBatch)
      } else {
        setTimeout(() => processBatch(), 0)
      }
    }

    img.onerror = () => {
      setError('精灵图加载失败')
      setIsProcessing(false)
      processingRef.current = false
    }

    img.src = spriteSrc

    return () => {
      processingRef.current = false
    }
  }, [spriteSrc])

  return { processedSrc, isProcessing, progress, error }
}
